<template>
  <v-container flex :style="{ marginTop: '10px' }">
    <v-data-table
      :headers="headers"
      :items="milestones"
      v-model="selectedPayments"
      item-key="id"
      :single-expand="false"
      show-expand
      :show-select="canUserEdit"
      :sort-by.sync="sortBy"
    >
      <template v-slot:top v-if="remainingValue !== 0">
        <v-alert
          dense
          outlined
          :type="alertType"
          :style="{
            margin: '0 20%',
            width: '60%'
          }"
        >
          {{remainingValueText}}
          <div v-if="parts.length > 1">{{remainingValueForPartsText}}</div>
        </v-alert>
      </template>
      <template v-slot:item.name="{ item }">
        <v-chip
          small
          :color="getChipColor(item)"
        >
          {{item.name}}
        </v-chip>
      </template>
      <template v-slot:item.description="{ item }">
        <small>{{item.description }}</small>
      </template>
      <template v-slot:item.comment="{ item }">
        <small>{{item.comment}}</small>
      </template>
      <template v-slot:item.percentOfContractPrice="{ item }">
        <span class="nowrap" v-if="!item.variationId && !item.liquidatedDamageId">
          {{item.percentOfContractPrice | toFixed }}%
        </span>
      </template>
      <template v-slot:item.date="{ item }">
        <span class="nowrap">{{item.date | formatDate}}</span>
      </template>
      <template v-slot:item.paymentAmountCents="{ item }">
        <PricesDisplay
          :prices="item.paymentAmountCents"
          :contract-currencies="currencies"
          :chosen-currency="chosenCurrency"
          :item-currency="item.currency"
        />
      </template>
      <template v-slot:item.application="{ item }">
        <small class="nowrap d-block">
          <v-icon small :color="item.amountAssignedToApplication.sum ? 'blue' : ''">mdi-plus</v-icon>
          {{item.amountAssignedToApplication.sum | formatCurrency(baseCurrency) }}
        </small>
        <small class="nowrap d-block">
          <v-icon small :color="item.amountPaid.sum ? 'success' : ''">mdi-check</v-icon>
          {{item.amountPaid.sum | formatCurrency(baseCurrency) }}
        </small>
      </template>
      <template v-slot:item.part="{ item }">
        <div v-if="item.part">
          {{item.part.name}}
          <small class="d-block text--secondary">{{item.purchaseOrder}}</small>
        </div>
        <div v-else>
          {{ $t(partTranslationKey + '.all') }}
          <small class="d-block text--secondary">{{item.purchaseOrder}}</small>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          @click="handleEditPaymentModal(item)"
          :disabled="!canUserEdit"
        >
          <v-icon>mdi-pencil </v-icon>
        </v-btn>
        <SubmitButton
          icon="mdi-delete"
          small
          :disabled="!!item.amountAssignedToApplication.sum || !canUserEdit || !!item.variationId || !!item.liquidatedDamageId"
          @click="$emit('delete-payment', item)"
        />
        <v-btn
          icon
          small
          :disabled="!!item.amountAssignedToApplication.sum  || !canUserEdit || !!item.liquidatedDamageId"
          @click="handleSplitPayment(item)"
        >
          <v-icon>mdi-call-split </v-icon>
        </v-btn>
      </template>
      <template  v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="subpayments-table">
          <SubMilestonesTable
            :payments="item.subMilestones"
            v-model="selectedSubPayments[item.id]"
            :key="key"
            :payment="item"
            :contract-price="contractPrice"
            :base-currency="baseCurrency"
            :payment-applications="paymentApplications"
            @update-submilestone="(data) => $emit('update-submilestone', data)"
            @delete-submilestone="(data) => $emit('delete-submilestone', data)"
            :can-user-edit="canUserEdit"
            :parts="parts"
            :chosen-currency="chosenCurrency"
            :check-name-duplicate="checkNameDuplicateForSubMilestones"
            :is-multi-currency="isMultiCurrency"
            :currencies="currencies"
          />
        </td>
      </template>
      <template v-slot:footer.prepend v-if="canUserEdit">
        <v-container
          v-if="showFooterButtons"
          align="baseline"
          class="mt-4"
        >
          <v-row class="pb-2" v-if="createApplicationError">
            <span class="red--text">{{ $t('payment.application.addError') }}</span>
          </v-row>
          <v-row justify="start">
              <v-btn
                rounded
                small
                @click="createApplication"
                color="warning"
                class="mr-2 mt-2"
                v-if="isAnyPaymentSelected"
              >
                {{ $t('payment.application.add') }}
              </v-btn>
              <v-btn
                rounded
                small
                @click="handleAddDaysToSelected(3)"
                color="primary"
                class="mr-2 mt-2"
              >
                {{ $t('payment.milestone.addDays', { number: 3 }) }}
              </v-btn>
              <v-btn
                rounded
                small
                @click="handleAddDaysToSelected(7)"
                color="primary"
                class="mr-2 mt-2"
              >
                {{ $t('payment.milestone.addDays', { number: 7 }) }}
              </v-btn>
              <v-text-field
                v-model="days"
                outlined
                type="number"
                min="0"
                :label="$t('payment.milestone.addCustomDays')"
                :style="{maxWidth: '95px'}"
                dense
              />
              <v-btn
                rounded
                small
                color="primary"
                @click="handleAddDaysToSelected(days)"
                class="ml-2 mt-2"
                :disabled="!days"
              >
                {{ $t('payment.milestone.addDays', { number: days }) }}
              </v-btn>
            <v-spacer></v-spacer>
          </v-row>
        </v-container>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import dayjs from 'dayjs'
import { mapState } from 'vuex'
import {
  calculateDisplayDateForMilestone,
  prepareSelectedMilestonesItem,
  calculateApplicationMilestoneValues,
  updatePaymentMilestone,
  calculateMaxAmountsForPaymentForm,
  checkMilestonesNamesDuplicates
} from '@/services'
import { DATE_FORMAT } from '@/helpers'
import { getLocalizationContext } from '@/helpers/translations'

export default {
  name: 'PaymentMilestonesTable',
  props: {
    value: {
      type: Array,
      default: () => ([])
    },
    contractPrice: {
      type: Object,
      default: () => ({ sum: 0 })
    },
    baseCurrency: {
      type: Object,
      default: () => ({})
    },
    currencies: Array,
    paymentApplications: Array,
    remainingValue: Number,
    parts: Array,
    chosenCurrency: Object,
    isMultiCurrency: Boolean
  },
  data () {
    return {
      sortBy: ['liquidatedDamageId', 'variationId'],
      days: null,
      selectedPayments: [],
      selectedSubPayments: {},
      showFooterButtons: false,
      createApplicationError: false,
      key: 0,
      headers: [
        { text: this.$t('common.name'), value: 'name' },
        { text: this.$t('common.description'), value: 'description' },
        { text: this.$t('payment.milestone.percentOfContract'), value: 'percentOfContractPrice' },
        { text: this.$t('payment.milestone.payment'), value: 'paymentAmountCents' },
        { text: this.$t('payment.milestone.inApplication'), value: 'application' },
        { text: this.$t('payment.milestone.forecastedDate'), value: 'date' },
        { text: this.$t('payment.milestone.substantiation'), value: 'comment' },
        { text: this.$tc(getLocalizationContext(this.isMultiCurrency) + '.self', 1), value: 'part' },
        { text: '', value: 'actions', sortable: false, width: 120 },
        { text: '', value: 'data-table-expand' }
      ]
    }
  },
  computed: {
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    milestones () {
      return this.value.map(payment => {
        const amounts = calculateApplicationMilestoneValues(this.paymentApplications, payment, this.currencies)
        const percentOfContractPrice = isNaN(payment.paymentAmountCents)
          ? (payment.paymentAmountCents.sum / this.contractPrice.sum * 100) * payment.currency.value
          : (payment.paymentAmountCents / this.contractPrice.sum * 100) * payment.currency.value
        return {
          ...payment,
          date: calculateDisplayDateForMilestone(this.paymentApplications, { ...payment, ...amounts }),
          subMilestonesAmount: payment.subMilestones.reduce((acc, curr) => acc + curr.paymentAmountCents, 0),
          percentOfContractPrice,
          ...amounts,
          subMilestonesWithDifferentParts: [...new Set(payment.subMilestones.map(i => i.partId))].length > 1
        }
      })
    },
    alertType () {
      return this.remainingValue > 0 ? 'warning' : 'error'
    },
    remainingValueForPartsText () {
      return this.parts.map(part => this.$t('payment.milestone.notCoveredParts', {
        name: part.name,
        value: part.formattedRemainingValue,
        percent: Math.round(part.remainingValue * 10000 / part.signedPriceCents) / 100
      })).join(', ')
    },
    remainingValueText () {
      const percent = this.remainingValue > 0
        ? this.$options.filters.toFixed(this.remainingValue / this.contractPrice.sum * 100)
        : this.$options.filters.toFixed((this.remainingValue / this.contractPrice.sum * 100) * -1 + 100)
      return this.remainingValue > 0
        ? this.$t('payment.milestone.milestonesNotCover', { remainingValue: this.$options.filters.formatCurrency(this.remainingValue), percent })
        : this.$t('payment.milestone.milestonesCoverMore', { percent })
    },
    isAnyPaymentSelected () {
      return Object.values(this.selectedSubPayments).some(i => i.length > 0) || this.selectedPayments.filter(i => i.subMilestones.length === 0).length
    },
    isSelectedMilestonesSameCurrency () {
      const paymentMilestones = this.getAllSelectedMilestones()
      if (paymentMilestones.length > 0) {
        const currency = paymentMilestones[0].currency
        return paymentMilestones.filter(item => item.currency.key === currency.key).length === paymentMilestones.length
      }
      return true
    },
    partTranslationKey () {
      return getLocalizationContext(this.isMultiCurrency)
    }
  },
  methods: {
    createApplication () {
      const paymentMilestones = this.getAllSelectedMilestones()
      if (this.isSelectedMilestonesSameCurrency) {
        this.$emit('add-application', paymentMilestones)
        this.selectedSubPayments = {}
      } else {
        this.createApplicationError = true
      }
    },
    getAllSelectedMilestones () {
      return [
        ...Object.values(this.selectedSubPayments).flat(),
        ...this.selectedPayments.filter(i => i.subMilestones.length === 0)
      ].map(item => {
        const newItem = prepareSelectedMilestonesItem(this.paymentApplications, item, this.currencies)
        newItem.paymentPart = newItem.percentToPay
        newItem.partAmountCents = newItem.paymentAmountCents * (Number(newItem.paymentPart) / 100)
        return newItem
      }).flat()
    },
    async handleAddDaysToSelected (days) {
      const milestonesToUpdate = [...this.selectedPayments, ...Object.values(this.selectedSubPayments)].flat()
      await Promise.all(milestonesToUpdate.map(milestone => {
        milestone.forecastedDate = dayjs(milestone.forecastedDate, DATE_FORMAT).add(days, 'day').format(DATE_FORMAT)
        return updatePaymentMilestone(milestone)
      }))
      this.$emit('milestones-update')
      this.selectedPayments = []
      this.selectedSubPayments = {}
    },
    handleSplitPayment (milestone) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: milestone.multiCurrencyMilestone ? 'SubMilestonesValuesForm' : 'SubMilestonesPercentForm',
        options: { maxWidth: 1200 },
        scope: {
          payment: milestone,
          paymentApplications: this.paymentApplications,
          contractPrice: this.contractPrice,
          parts: this.parts,
          currencies: this.currencies,
          isMultiCurrency: this.isMultiCurrency
        },
        handlers: {
          saveSubPayments: (submilestones) => this.$emit('update-submilestones', { milestone, submilestones })
        }
      })
    },
    checkNameDuplicateForSubMilestones (milestone, item) {
      const array = this.value.map(i => ({ ...i, subMilestones: i.subMilestones.filter(j => j.name !== item.name) }))
      return checkMilestonesNamesDuplicates(milestone, array, 1)
    },
    checkNameDuplicate (milestone, item) {
      return checkMilestonesNamesDuplicates(milestone, this.value.filter(i => i.name !== item.name), 1)
    },
    handleEditPaymentModal (item) {
      const { maxAmountsPerCurrency, maxAmountsPerPart } = calculateMaxAmountsForPaymentForm(this.value, this.contractPrice, this.currencies, this.parts)
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'PaymentMilestoneForm',
        options: { maxWidth: 850 },
        scope: {
          title: this.$t('payment.milestone.editMilestone'),
          payment: item,
          maxAmountsPerCurrency,
          maxAmountsPerPart,
          contractPrice: this.contractPrice,
          currencies: this.currencies,
          isVariation: !!item.variationId && !!item.liquidatedDamageId,
          parts: this.parts,
          checkNameDuplicate: (milestone) => this.checkNameDuplicate(milestone, item),
          isMultiCurrency: this.isMultiCurrency
        },
        handlers: {
          savePayment: async (data) => {
            await updatePaymentMilestone(data)
            this.$emit('milestones-update')
          },
          mandatorySubmilestones: (data) => this.$emit('mandatorySubmilestones', data)
        }
      })
    },
    getChipColor (item) {
      if (item.variationId) {
        return 'primary'
      }
      if (item.liquidatedDamageId) {
        return 'yellow'
      }
      return 'orange'
    }
  },
  watch: {
    days (v) {
      if (v < 0) {
        this.days = 0
      }
    },
    selectedPayments (v) {
      const isAnySelected = Object.keys(this.selectedSubPayments).some(i => this.selectedSubPayments[i].length > 0)
      this.showFooterButtons = v.length > 0 || isAnySelected
      if (this.createApplicationError) {
        this.createApplicationError = !this.isSelectedMilestonesSameCurrency
      }
    },
    selectedSubPayments: {
      deep: true,
      handler (v) {
        const isAnySelected = Object.keys(v).some(i => v[i].length > 0)
        this.showFooterButtons = this.selectedPayments.length > 0 || isAnySelected
        if (this.createApplicationError) {
          this.createApplicationError = !this.isSelectedMilestonesSameCurrency
        }
      }
    }
  }
}
</script>

<style>
.subpayments-table {
  border-left: medium solid #E0E0E0;
  padding: 0 !important;
}
.subpayments-table > .container {
  padding: 0;
}
</style>
